import React, { useEffect, useState } from "react";
import Notices from "./modalComponents/Notices";
import axios from "axios";

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState("live"); // 'live' | 'video' | 'general'
  const [outstandingPayments, setOutstandingPayments] = useState([]);
  const [printMaterials, setPrintMaterials] = useState([]);
  const [absenceNotices, setAbsenceNotices] = useState([]);
  const [pendingStudents, setPendingStudents] = useState([]);
  const [homeworkTracker, setHomeworkTracker] = useState([]);
  const [noShowStudents, setNoShowStudents] = useState([]);
  
  const [loadingAbsences, setLoadingAbsences] = useState(true);
  const [loadingPending, setLoadingPending] = useState(true);
  const [loadingHomework, setLoadingHomework] = useState(true);
  const [loadingNoShow, setLoadingNoShow] = useState(true);

  useEffect(() => {
    const fetchOutstandingPayments = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getOutstandingPayments`);
        setOutstandingPayments(response.data.data);
      } catch (error) {
        console.error("Error fetching outstanding payments:", error);
      }
    };

    const fetchPrintMaterials = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getTodayPrintMaterials`);
        setPrintMaterials(response.data.data);
      } catch (error) {
        console.error("Error fetching print materials:", error);
      }
    };

    const fetchAbsenceNotices = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getAbsenceNotices`);
        if (response.data.success) {
          setAbsenceNotices(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching absence notices:", error);
      } finally {
        setLoadingAbsences(false);
      }
    };

    const fetchPendingStudents = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/pending/students`);
        if (response.data.success) {
          setPendingStudents(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching pending students:", error);
      } finally {
        setLoadingPending(false);
      }
    };

    const fetchHomeworkTracker = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getDailyHomeworkTracker`);
        if (response.data.success) {
          setHomeworkTracker(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching daily homework tracker:", error);
      } finally {
        setLoadingHomework(false);
      }
    };

    const fetchNoShowStudents = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getNoShowStudents`);
        if (response.data.success) {
          setNoShowStudents(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching no-show students:", error);
      } finally {
        setLoadingNoShow(false);
      }
    };

    fetchPendingStudents();
    fetchOutstandingPayments();
    fetchPrintMaterials();
    fetchAbsenceNotices();
    fetchHomeworkTracker();
    fetchNoShowStudents();
  }, []);

  const handleApprove = async (pendingId) => {
    if (!window.confirm("Are you sure you want to approve this student application?")) return;

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/approve/student/${pendingId}`);
      if (response.data.success) {
        setPendingStudents((prev) => prev.filter((student) => student.id !== pendingId));
        alert("Student application approved successfully.");
      } else {
        alert("Failed to approve student.");
      }
    } catch (error) {
      console.error("Error approving student:", error);
      alert("Failed to approve student. Please try again.");
    }
  };

  const handleReject = async (pendingId) => {
    if (!window.confirm("Are you sure you want to reject this student application?")) return;

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/reject/student/${pendingId}`);
      if (response.data.success) {
        setPendingStudents((prev) => prev.filter((student) => student.id !== pendingId));
        alert("Student application rejected successfully.");
      } else {
        alert("Failed to reject student.");
      }
    } catch (error) {
      console.error("Error rejecting student:", error);
      alert("Failed to reject student. Please try again.");
    }
  };

  const handleMarkRead = async (recordId, recordType, studentId) => {
    if (window.confirm("Mark this absence as read?")) {
      try {
        const response = await axios.patch(`${process.env.REACT_APP_API_URL}/admin/markAbsenceRead`, {
          record_id: recordId,
          record_type: recordType,
          student_id: studentId,
        });
        if (response.data.success) {
          setAbsenceNotices((prev) =>
            prev.filter((record) => {
              const lessonId = record.record_type === "live" ? record.live_lesson_id : record.video_lesson_id;
              return !(record.student_id === studentId && lessonId === recordId);
            })
          );
          alert("Absence marked as read.");
        }
      } catch (error) {
        console.error("Error marking absence as read:", error);
        alert("Failed to mark as read.");
      }
    }
  };

  // Distinct handler for Live confirmations
  const handleMarkAllLiveRead = async () => {
    if (window.confirm("Mark all live absences as read?")) {
      try {
        const response = await axios.patch(`${process.env.REACT_APP_API_URL}/admin/markAllLiveAbsenceRead`);
        if (response.data.success) {
          setAbsenceNotices((prev) => prev.filter((r) => r.record_type !== "live"));
          alert("All live absences marked as read.");
        }
      } catch (error) {
        console.error("Error marking live absences:", error);
        alert("Failed to mark live absences as read.");
      }
    }
  };

  // Distinct handler for Video confirmations
  const handleMarkAllVideoRead = async () => {
    if (window.confirm("Mark all video absences as read?")) {
      try {
        const response = await axios.patch(`${process.env.REACT_APP_API_URL}/admin/markAllVideoAbsenceRead`);
        if (response.data.success) {
          setAbsenceNotices((prev) => prev.filter((r) => r.record_type !== "video"));
          alert("All video absences marked as read.");
        }
      } catch (error) {
        console.error("Error marking video absences:", error);
        alert("Failed to mark video absences as read.");
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    let date = new Date(dateString);
    if (isNaN(date.getTime())) {
      const parts = dateString.split("-");
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          date = new Date(year, month, day);
        }
      }
    }
    if (isNaN(date.getTime())) return "N/A";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Check if today >= due date for homework highlighting
  const isHomeworkOverdue = (dueDateString) => {
    if (!dueDateString) return false;
    const todayStr = new Date().toISOString().split("T")[0];
    const formattedDueDate = formatDate(dueDateString);
    return todayStr >= formattedDueDate;
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case "live":
        return "Dashboard - Live";
      case "video":
        return "Dashboard - Video";
      case "general":
        return "Dashboard - General";
      default:
        return "Admin Dashboard";
    }
  };

  // Filtered lists for live and video absences
  const liveAbsences = absenceNotices.filter((r) => r.record_type === "live");
  const videoAbsences = absenceNotices.filter((r) => r.record_type === "video");

  return (
    <div className="w-full h-full min-h-screen bg-white flex flex-col p-6 overflow-y-auto box-border rounded-xl shadow-sm">
      {/* Header & Tabs Navigation */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 pb-4 border-b border-gray-200 gap-4 shrink-0 w-full">
        <div>
          <h1 className="text-3xl font-extrabold text-neutral-900 tracking-tight">
            {getTabTitle()}
          </h1>
        </div>
        
        {/* Tab Buttons */}
        <div className="flex bg-gray-100 p-1 rounded-lg shadow-inner border border-gray-200">
          <button
            onClick={() => setActiveTab("live")}
            className={`px-6 py-2 rounded-md font-medium text-sm transition-all ${
              activeTab === "live" ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-600 hover:text-neutral-900"
            }`}
          >
            Live
          </button>
          <button
            onClick={() => setActiveTab("video")}
            className={`px-6 py-2 rounded-md font-medium text-sm transition-all ${
              activeTab === "video" ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-600 hover:text-neutral-900"
            }`}
          >
            Video
          </button>
          <button
            onClick={() => setActiveTab("general")}
            className={`px-6 py-2 rounded-md font-medium text-sm transition-all ${
              activeTab === "general" ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-600 hover:text-neutral-900"
            }`}
          >
            General
          </button>
        </div>
      </div>

      {/* TAB CONTENT WRAPPER */}
      <div className="flex-1 flex items-center justify-center w-full my-auto">
        {/* TAB CONTENT: LIVE */}
        {activeTab === "live" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full animate-fadeIn items-stretch">
            {/* Leave Notices */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 flex flex-col min-h-[520px] w-full">
              <Notices />
            </div>

            {/* Live Absence Notices Only */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 flex flex-col min-h-[520px] w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-red-700">Live Absence Notices</h2>
                {liveAbsences.length > 0 && (
                  <button
                    onClick={handleMarkAllLiveRead}
                    className="bg-red-500 text-white px-3 py-1 rounded-md hover:bg-red-600 text-xs font-semibold shadow-sm transition"
                  >
                    Confirm All
                  </button>
                )}
              </div>
              {loadingAbsences ? (
                <p className="text-gray-500 text-sm">Loading absence notices...</p>
              ) : liveAbsences.length > 0 ? (
                <div className="flex-1 overflow-y-auto text-sm space-y-3 pr-1 max-h-[420px]">
                  {liveAbsences.map((record, index) => (
                    <div key={index} className="p-3 bg-neutral-50 rounded-lg border border-gray-100 flex flex-col gap-1">
                      <p className="font-semibold text-neutral-800">{record.student_name} ({record.student_id})</p>
                      <p className="text-gray-500 text-xs">Live Lesson ID: {record.live_lesson_id}</p>
                      <p className="text-gray-500 text-xs">Date: {formatDate(record.date)}</p>
                      <button
                        onClick={() => handleMarkRead(record.live_lesson_id, "live", record.student_id)}
                        className="mt-2 self-start bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs font-medium transition"
                      >
                        Mark as Read
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No unread live absence notices.</p>
              )}
            </div>

            {/* Today's Print Materials */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 flex flex-col min-h-[520px] w-full">
              <h2 className="text-xl font-bold text-blue-700 mb-4">Today's Print Materials</h2>
              {printMaterials.length > 0 ? (
                <div className="flex-1 text-gray-700 overflow-y-auto text-sm space-y-4 pr-1 max-h-[420px]">
                  {printMaterials.map((item) => (
                    <div key={item.class_id} className="pb-3 border-b border-gray-100 last:border-0">
                      <h3 className="font-semibold text-neutral-800 text-base">{item.class_name}</h3>
                      <p className="text-gray-600 mt-1">
                        <span className="font-medium text-gray-700">Materials: </span>
                        {item.printing_materials}
                      </p>
                      <p className="font-medium text-gray-700 mt-2">Enrolled Students:</p>
                      {item.students.length > 0 ? (
                        <ul className="list-disc pl-5 text-gray-600 mt-1 space-y-0.5">
                          {item.students.map((student) => (
                            <li key={student.student_id}>
                              {student.student_name} ({student.student_id})
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-500 text-xs mt-1">No students enrolled.</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No classes starting today requiring printing.</p>
              )}
            </div>
          </div>
        )}

        {/* TAB CONTENT: VIDEO */}
        {activeTab === "video" && (
          <div className="w-full grid grid-cols-1 lg:grid-cols-1 gap-6 animate-fadeIn items-stretch">
            {/* Video Absences Container */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 flex flex-col min-h-[500px] w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-red-700">Video Absence Notices</h2>
                {videoAbsences.length > 0 && (
                  <button
                    onClick={handleMarkAllVideoRead}
                    className="bg-red-500 text-white px-3 py-1 rounded-md hover:bg-red-600 text-xs font-semibold shadow-sm transition"
                  >
                    Confirm All
                  </button>
                )}
              </div>
              {loadingAbsences ? (
                <p className="text-gray-500 text-sm">Loading video absences...</p>
              ) : videoAbsences.length > 0 ? (
                <div className="flex-1 overflow-y-auto text-sm space-y-3 pr-1 max-h-[420px]">
              {videoAbsences.map((record, index) => (
                <div key={index} className="p-3 bg-neutral-50 rounded-lg border border-gray-100 flex flex-col gap-1">
                  {/* Full name */}
                  <p className="font-semibold text-neutral-800">
                    {record.student_name}
                  </p>

                  {/* Phone */}
                  <p className="text-gray-600 text-sm">
                    Phone: {record.phone_number || "N/A"}
                  </p>

                  {/* Time slot */}
                  <p className="text-gray-600 text-sm">
                    Time: {record.time_slot || "N/A"}
                  </p>

                  {/* Course + Lesson */}
                  <p className="text-gray-600 text-sm">
                    Course: {record.course_lesson || "N/A"}
                  </p>

                  <button
                    onClick={() => handleMarkRead(record.video_lesson_id, "video", record.student_id)}
                    className="mt-2 self-start bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs font-medium transition"
                  >
                    Mark as Read
                  </button>
                </div>
              ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No unread video absence notices.</p>
              )}
            </div>

            {/* Daily Homework Tracker Container
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 flex flex-col min-h-[500px] w-full">
              <h2 className="text-xl font-bold text-indigo-700 mb-4">Daily Homework Tracker</h2>
              {loadingHomework ? (
                <p className="text-gray-500 text-sm">Loading homework tracker...</p>
              ) : homeworkTracker.length > 0 ? (
                <div className="flex-1 overflow-y-auto text-sm space-y-3 pr-1 max-h-[420px]">
                  {homeworkTracker.map((item, index) => {
                    const isOverdue = isHomeworkOverdue(item.due_date);
                    return (
                      <div 
                        key={index} 
                        className={`p-3 rounded-lg border transition-colors flex flex-col gap-1 ${
                          isOverdue ? "bg-red-50 border-red-200" : "bg-neutral-50 border-gray-100"
                        }`}
                      >
                        <p className="font-semibold text-neutral-800">
                          {item.student_name} <span className="text-xs text-gray-500">({item.studsent_id})</span>
                        </p>
                        <p className="text-xs text-gray-600"><span className="font-medium">Lesson Time:</span> {item.lesson_time}</p>
                        <p className="text-xs text-gray-600"><span className="font-medium">Pending Homework:</span> {item.pending_homework}</p>
                        <p className="text-xs text-gray-600"><span className="font-medium">Related Course:</span> {item.related_course}</p>
                        <p className={`text-xs font-semibold ${isOverdue ? "text-red-700" : "text-gray-600"}`}>
                          <span>Due Date:</span> {formatDate(item.due_date)} {isOverdue && "(Overdue)"}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No homework data for students coming today.</p>
              )}
            </div> */}
          </div>
        )}

        {/* TAB CONTENT: GENERAL */}
        {activeTab === "general" && (
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 w-full animate-fadeIn items-stretch">
            {/* Pending Student Applications */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 flex flex-col min-h-[500px] w-full">
              <h2 className="text-xl font-bold text-amber-700 mb-4">Pending Student Applications</h2>
              {loadingPending ? (
                <p className="text-gray-500 text-sm">Loading pending applications...</p>
              ) : pendingStudents.length > 0 ? (
                <div className="flex-1 overflow-y-auto space-y-3 pr-1 max-h-[420px]">
                  {pendingStudents.map((student) => (
                    <div
                      key={student.id}
                      className="p-4 bg-amber-50/55 border border-amber-200 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3"
                    >
                      <div>
                        <p className="font-semibold text-neutral-800">
                          {student.first_name} {student.surname}
                          {student.chinese_name && ` (${student.chinese_name})`}
                        </p>
                        <p className="text-gray-500 text-xs mt-0.5">
                          {student.school || "School N/A"} • Form {student.grade || "N/A"} • Applied: {formatDate(student.created_at || student.c_time)}
                        </p>
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto justify-end">
                        <button
                          onClick={() => handleApprove(student.id)}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-xs font-semibold shadow-sm transition"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(student.id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md text-xs font-semibold shadow-sm transition"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No pending applications.</p>
              )}
            </div>

            {/* No-Show Students
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 flex flex-col min-h-[500px] w-full">
              <h2 className="text-xl font-bold text-rose-700 mb-4">No-Show Students</h2>
              {loadingNoShow ? (
                <p className="text-gray-500 text-sm">Loading no-show records...</p>
              ) : noShowStudents.length > 0 ? (
                <div className="flex-1 overflow-y-auto space-y-3 pr-1 max-h-[420px]">
                  {noShowStudents.map((item, index) => (
                    <div
                      key={index}
                      style={{ backgroundColor: item.bg_color || "#f9fafb" }}
                      className="p-4 border border-gray-200 rounded-lg flex flex-col gap-1 shadow-sm"
                    >
                      <p className="font-semibold text-neutral-900">
                        {item.student_name} <span className="text-xs text-gray-500">({item.student_id})</span>
                      </p>
                      <p className="text-xs text-neutral-700"><span className="font-medium">Current Course:</span> {item.current_video_course}</p>
                      <p className="text-xs text-neutral-700"><span className="font-medium">No-Showing Day:</span> {item.no_showing_day}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No recent no-show student records.</p>
              )}
            </div>

            {/* Underperforming Students */}
            {/* <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 flex flex-col min-h-[500px] w-full">
              <h2 className="text-xl font-bold text-purple-700 mb-4">Underperforming Students</h2>
              <div className="flex-1 flex flex-col items-center justify-center text-center p-6 bg-neutral-50 rounded-lg border border-dashed border-gray-200">
                <p className="text-gray-500 text-sm">
                  Logic pending. Underperforming metrics and tracking features will be displayed here.
                </p>
              </div>
            </div> */} 
          </div>
        )}
      </div>
    </div>
  );
}